import { nonCustomisableColours } from "..";
import { parseRgb } from "./parseRgb";

/**
 * Applies an opacity value to the provided hex colour code, returning a css
 * rgba colour string.
 *
 * @param hex A valid hexadecimal colour code. Accepts 3- or 6-digit codes. Does
 *            not accept colour codes which includes alpha values.
 * @param opacity The decimal opacity value to be applied. Must be between 0 and
 *            1 inclusive.
 * @param returnOpaqueColour Boolean value for whether the function should
 *            return an opaque RGB colour equivalent to if the semi-transparent
 *            foreground is on top of a `backgroundColour` background (if
 *            true) or a semi-transparent RGBA colour. Defaults to true, because
 *            we rarely ever want to actually use transparency with our colours.
 *            Using opaque colour equivalents lets us match colours regardless
 *            of their background and context, while still using "opacity" in
 *            the design system
 * @param backgroundColour The hex colour the opaque equivalent is composited
 *            against. Defaults to white (`nonCustomisableColours.white`) to
 *            preserve existing behaviour for call sites that don't render
 *            against the brand dark/light theme surface (`--d-surface`).
 *            Callers that DO render against a themed surface (e.g. Button.tsx
 *            variants, which composite washes/disabled-states on top of
 *            `theme.brand.cssVars["--d-surface"]`) should pass that surface
 *            colour explicitly — otherwise, in dark theme, the hardcoded
 *            white default produces washes that look wrong/inverted against
 *            a dark surface. Has no effect when `returnOpaqueColour` is false.
 * @returns A css rgb or rgba colour string (depending on the value
 *          of returnOpaqueColour)
 */
export function applyOpacity(
  hex: string,
  opacity: number,
  returnOpaqueColour = true,
  backgroundColour: string = nonCustomisableColours.white,
): string {
  if (!(0 <= opacity && opacity <= 1))
    throw new Error(
      "Invalid opacity value. Opacity must be a value between 0 and 1, inclusive.",
    );

  const rgb = parseRgb(hex);
  if (!returnOpaqueColour) return `rgba(${rgb.toString()},${opacity})`;

  const foreground = rgb;
  const background = parseRgb(backgroundColour);
  const opaqueEquivalent = foreground.map((foregroundRgbValue, index) =>
    Math.round(
      background[index] - opacity * (background[index] - foregroundRgbValue),
    ),
  );
  return `rgb(${opaqueEquivalent.toString()})`;
}
